/**
 * Cross-screen UI state: which modal is open, the shift, the fiscal badge.
 *
 * Modals live here rather than in component state because several of them are
 * opened from more than one place - the payment dialog from the Pay button and
 * from a keyboard shortcut, the customer picker from the header and from the
 * delivery flow.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { shiftApi, fiscalApi } from '../services/endpoints';

export const loadShift = createAsyncThunk('ui/loadShift', async () => shiftApi.current());

export const loadFiscalStatus = createAsyncThunk('ui/fiscalStatus', async () => fiscalApi.status());

const initialState = {
    // Desktop only: the sidebar narrowed to its icons.
    sidebarCollapsed: false,

    // Below `lg` there is no room for a sidebar at all, so the same navigation
    // is an off-canvas drawer with its own open state. Kept separate from
    // `sidebarCollapsed` because the two mean opposite things - a collapsed
    // sidebar is still on screen, a closed drawer is not - and sharing one flag
    // made the drawer open itself on a desktop that had merely been narrowed.
    mobileNavOpen: false,

    isFullscreen: false,

    // One modal at a time; the value is the modal's own payload.
    modal: null,          // 'payment' | 'modifier' | 'hold' | 'discount' | 'customer' | 'table' | ...
    modalPayload: null,

    shift: null,
    shiftTotals: null,

    // Drives the badge on the FBR / PRA sidebar entry.
    fiscal: {
        enabled: false,
        activeAuthorityCode: null,
        pending: 0,
        failed: 0
    },

    // Set from the api layer's network errors, so the shell can show an offline
    // banner without every screen handling it.
    isServerReachable: true
};

const uiSlice = createSlice({
    name: 'ui',
    initialState,
    reducers: {
        toggleSidebar: (state) => { state.sidebarCollapsed = !state.sidebarCollapsed; },

        openMobileNav: (state) => { state.mobileNavOpen = true; },
        closeMobileNav: (state) => { state.mobileNavOpen = false; },
        toggleMobileNav: (state) => { state.mobileNavOpen = !state.mobileNavOpen; },
        setFullscreen: (state, action) => { state.isFullscreen = action.payload; },

        openModal: (state, action) => {
            state.modal = action.payload.modal;
            state.modalPayload = action.payload.payload ?? null;
        },
        closeModal: (state) => {
            state.modal = null;
            state.modalPayload = null;
        },

        setServerReachable: (state, action) => { state.isServerReachable = action.payload; },

        // Applied optimistically when an order is settled, so the fiscal badge
        // ticks up immediately rather than at the next poll.
        incrementFiscalPending: (state) => {
            if (state.fiscal.enabled) state.fiscal.pending += 1;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadShift.fulfilled, (state, action) => {
                state.shift = action.payload?.session ?? null;
                state.shiftTotals = action.payload?.totals ?? null;
            })
            .addCase(loadShift.rejected, (state) => {
                state.shift = null;
                state.shiftTotals = null;
            })
            .addCase(loadFiscalStatus.fulfilled, (state, action) => {
                state.fiscal = {
                    enabled: action.payload.enabled,
                    activeAuthorityCode: action.payload.activeAuthorityCode,
                    pending: action.payload.queue?.pending ?? 0,
                    failed: action.payload.queue?.failed ?? 0
                };
            });
    }
});

export const {
    toggleSidebar, openMobileNav, closeMobileNav, toggleMobileNav,
    setFullscreen, openModal, closeModal,
    setServerReachable, incrementFiscalPending
} = uiSlice.actions;

export const selectUi = (state) => state.ui;
export const selectModal = (state) => ({ modal: state.ui.modal, payload: state.ui.modalPayload });
export const selectShift = (state) => state.ui.shift;
export const selectFiscal = (state) => state.ui.fiscal;

export default uiSlice.reducer;
